import React, { useState } from 'react';
import { auth } from '../firebase';

const CHECKOUT_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/create-checkout-session-live';

const TIERS = [
  {
    id: 'starter', name: 'Starter', price: '€9.90', storage: 25, aiLabel: '1,000 AI ops/mo',
    unlocks: [
      { text: 'All 5 AI content packs', star: false, info: 'Finance, Legal, Medical, Contacts and General — every file automatically sorted into the right category.' },
      { text: 'Auto-categorization', star: false, info: 'Files are sorted by type (document, image, spreadsheet, presentation, code) the moment they land.' },
      { text: 'Location & date sorting', star: false, info: 'Files are organized by upload date and folder path automatically — no manual sorting needed.' },
      { text: 'Basic search', star: false, info: 'Search by filename, category, and upload date across your entire library.' },
    ],
    benefit: 'Everything you need to escape the chaos — your first 25 GB, fully organized and AI-classified.',
  },
  {
    id: 'basic', name: 'Basic', price: '€19.90', storage: 60, aiLabel: '3,000 AI ops/mo',
    unlocks: [
      { text: 'Everything in Starter', star: false, info: 'All features from the Starter plan included.' },
      { text: 'Bulk operations', star: true, info: 'Select multiple files at once — bulk download, bulk delete, bulk move. Saves hours on large libraries.' },
      { text: 'Tag-based filtering', star: false, info: 'Filter your library by AI-generated content tags — find all invoices, all contracts, all contact lists instantly.' },
      { text: 'Extracted text preview', star: false, info: 'See the first lines of extracted text for documents, spreadsheets and presentations directly in the file panel.' },
    ],
    benefit: 'Unlocks bulk actions — select, move and manage files at scale across 60 GB.',
  },
  {
    id: 'plus', name: 'Plus', price: '€29.90', storage: 100, aiLabel: '8,000 AI ops/mo',
    popular: true,
    unlocks: [
      { text: 'Everything in Basic', star: false, info: 'All features from the Basic plan included.' },
      { text: 'Advanced filters', star: false, info: 'Filter by content type, AI label, date range, file size and category simultaneously.' },
      { text: 'Invoice detection', star: true, info: 'AI automatically detects invoices in documents and images — flags them in the Finance content pack with extra confidence.' },
      { text: '100 GB data lake', star: false, info: 'Your full business data lake — documents, images, spreadsheets, code, configs, all in one place.' },
    ],
    benefit: 'The sweet spot for an active SME — 100 GB, invoice detection, and the filters to find anything instantly.',
  },
  {
    id: 'pro', name: 'Pro', price: '€49.90', storage: 200, aiLabel: '20,000 AI ops/mo',
    unlocks: [
      { text: 'Everything in Plus', star: false, info: 'All features from the Plus plan included.' },
      { text: 'Semantic search', star: true, info: 'Search by meaning, not just filename — "find all supplier agreements from last year" works even without exact keywords.' },
      { text: 'Smart Agent (V1.0.1)', star: true, info: 'Ask questions about your files in plain language. "Summarise all invoices from March" or "Who are my top suppliers?" — answered instantly.' },
      { text: '200 GB data lake', star: false, info: 'Double the storage of Plus — room for a growing team\'s full document history.' },
    ],
    benefit: 'Ask your files questions in plain language — Smart Agent and semantic search unlock in Pro.',
  },
  {
    id: 'premium', name: 'Premium', price: '€79.90', storage: 500, aiLabel: '40,000 AI ops/mo',
    unlocks: [
      { text: 'Everything in Pro', star: false, info: 'All features from the Pro plan included.' },
      { text: 'Priority support', star: true, info: 'Direct access to the DBOS team — faster response times and dedicated onboarding for your team.' },
      { text: '500 GB storage', star: false, bright: true, info: '500 GB — room for a full multi-year business archive across every file type.' },
      { text: '40,000 AI ops/month', star: false, bright: true, info: 'The highest AI classification volume available — processes a large team\'s uploads comfortably every month.' },
    ],
    benefit: 'Maximum capacity for serious operations — 500 GB, priority support, and 40K AI ops per month.',
  },
];

function pct(used, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((used / total) * 100));
}

function fillColor(p) {
  if (p >= 90) return '#ff6b6b';
  if (p >= 70) return '#ffa552';
  return '#d4af37';
}

function UpgradeModal({ gido, quota, onClose, onRefreshQuota }) {
  const [trialCode, setTrialCode] = useState('');
  const [codeStatus, setCodeStatus] = useState(null);
  const [codeApplying, setCodeApplying] = useState(false);

  async function applyTrialCode() {
    setCodeApplying(true);
    setCodeStatus(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        setCodeStatus({ ok: false, msg: 'Please sign in again and retry.' });
        setCodeApplying(false);
        return;
      }

      const res = await fetch('https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-extend-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken, code: trialCode.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setCodeStatus({ ok: true, msg: `Trial extended by ${data.days} days!` });
        if (onRefreshQuota) onRefreshQuota();
      } else {
        const map = {
          'invalid code': "That code isn't valid.",
          'trial already extended once': 'This account has already used a trial-extension code.',
          'only trial/free accounts can use a test code': 'Trial codes only apply to trial or free accounts.',
          'Invalid token': 'Please sign in again and retry.',
          'GIDO not found': 'Account not found. Please sign in again.',
        };
        setCodeStatus({ ok: false, msg: map[data.error] || 'Could not apply code.' });
      }
    } catch (e) {
      setCodeStatus({ ok: false, msg: 'Network error. Try again.' });
    }
    setCodeApplying(false);
  }

  const [loading, setLoading] = useState(null);

  const currentTier  = quota?.tier || 'trial';
  const usedGb       = quota?.used_gb || 0;
  const storageGb    = quota?.storage_gb || 25;
  const aiMonthUsed  = quota?.ai_labels_used_month || 0;
  const aiMonthTotal = quota?.ai_labels_per_month || 1000;
  const aiDayUsed    = quota?.ai_labels_used_day || 0;
  const aiDayTotal   = quota?.ai_labels_per_day || 100;

  const storagePct = pct(usedGb, storageGb);
  const monthPct   = pct(aiMonthUsed, aiMonthTotal);
  const dayPct     = pct(aiDayUsed, aiDayTotal);

  const handleUpgrade = async (tierId) => {
    setLoading(tierId);
    try {
      const referredBy = localStorage.getItem('dbos_referred_by');
      const res = await fetch(CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gido_id:    gido?.gido_id,
          tier:       tierId,
          return_to:  'smartdata',
          email:      auth.currentUser?.email,
          referred_by: referredBy || undefined,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        localStorage.removeItem('dbos_referred_by');
        window.location.href = data.checkout_url;
      } else {
        alert('Could not start checkout. Please try again.');
        setLoading(null);
      }
    } catch (e) {
      alert('Could not start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div className="upgrade-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-close" onClick={onClose}>✕</button>
        <h2>Choose your plan</h2>
        <p className="upgrade-subtitle">
          Every plan includes all AI categories — Finance, Legal, Medical, Contacts, General. The difference is volume, not capability.
        </p>

        <div className="usage-section">
          <div className="usage-row">
            <div className="usage-label">
              <span>📦 Storage</span>
              <span>{usedGb.toFixed(2)} / {storageGb} GB</span>
            </div>
            <div className="usage-bar">
              <div className="usage-fill" style={{ width: `${storagePct}%`, background: fillColor(storagePct) }} />
            </div>
          </div>
          <div className="usage-row">
            <div className="usage-label">
              <span>🏷️ AI classifications — this month</span>
              <span>{aiMonthUsed} / {aiMonthTotal}</span>
            </div>
            <div className="usage-bar">
              <div className="usage-fill" style={{ width: `${monthPct}%`, background: fillColor(monthPct) }} />
            </div>
          </div>
          <div className="usage-row">
            <div className="usage-label">
              <span>⚡ AI classifications — today</span>
              <span>{aiDayUsed} / {aiDayTotal}</span>
            </div>
            <div className="usage-bar">
              <div className="usage-fill" style={{ width: `${dayPct}%`, background: fillColor(dayPct) }} />
            </div>
          </div>
          {currentTier === 'trial' && (
            <p className="usage-note">
              💡 During your trial we keep daily AI volume modest so you can explore every capability risk-free —
              every file type, every content category, fully working. Upgrading lifts these caps and gives you the
              monthly volume that matches how much you process.
            </p>
          )}
        </div>

        <div className="upgrade-tiers">
          {TIERS.map(tier => {
            const isCurrent = currentTier === tier.id;
            return (
              <div key={tier.id} className={`upgrade-tier ${tier.popular ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}>
                {tier.popular && !isCurrent && <div className="upgrade-badge">Most popular</div>}
                {isCurrent && <div className="upgrade-badge current-badge">Current plan</div>}
                <div className="upgrade-tier-name">{tier.name}</div>
                <div className="upgrade-tier-price">{tier.price}<span>/mo</span></div>
                <div className="upgrade-tier-spec">{tier.storage} GB storage</div>
                <div className="upgrade-tier-spec">{tier.aiLabel}</div>
                <ul className="upgrade-tier-unlocks">
                  {tier.unlocks.map((u, i) => (
                    <li key={i} className={`${u.star ? 'unlock-highlight' : ''} ${u.bright ? 'unlock-bright' : ''}`}>
                      {u.star ? '✦ ' : ''}{u.text}
                      {u.info && (
                        <span className="unlock-info-wrap">
                          <span className="unlock-info-icon">ⓘ</span>
                          <span className="unlock-tooltip">{u.info}</span>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="upgrade-tier-benefit">{tier.benefit}</div>
                <button
                  className="upgrade-tier-btn"
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={loading !== null || isCurrent}
                >
                  {isCurrent ? 'Current plan' : loading === tier.id ? 'Redirecting…' : 'Select'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="trial-code-box">
          <span className="trial-code-label">Have a test voucher?</span>
          <p className="trial-code-hint">
            A test voucher extends your free trial. Enter it here to apply it right away.
          </p>
          <div className="trial-code-row">
            <input
              type="text"
              value={trialCode}
              onChange={(e) => setTrialCode(e.target.value.toUpperCase())}
              placeholder="Enter voucher code"
              className="trial-code-input"
            />
            <button className="trial-code-btn" onClick={applyTrialCode} disabled={codeApplying || !trialCode.trim()}>
              {codeApplying ? '...' : 'Apply'}
            </button>
          </div>
          {codeStatus && (
            <span className={codeStatus.ok ? 'trial-code-ok' : 'trial-code-err'}>{codeStatus.msg}</span>
          )}
          <p className="trial-code-promo-note">
            Have a discount code instead? Choose a plan above and select it — you can enter the code at checkout.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;